
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Clock, 
  Book, 
  CheckCircle2, 
  Circle, 
  ChevronRight,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { PageLayout } from '@/components/layout/PageLayout';
import { getLearningPathById } from '@/services/learning-service';
import { LearningPath } from '@/types/learning';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const LearningPathDetail = () => {
  const { pathId } = useParams<{ pathId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [path, setPath] = useState<LearningPath | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  useEffect(() => {
    const fetchPathDetails = async () => {
      if (!pathId) return;
      
      try {
        const pathData = await getLearningPathById(pathId, user?.id);
        setPath(pathData);
      } catch (error) {
        console.error('Error fetching learning path details:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load learning path details",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPathDetails();
  }, [pathId, user?.id, toast]);

  const handleStartModule = (moduleId: string) => {
    navigate(`/module/${moduleId}`);
  };

  if (isLoading) {
    return (
      <PageLayout>
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="animate-pulse bg-muted h-8 w-48 mb-4 rounded" />
          <div className="animate-pulse bg-muted h-32 w-full mb-6 rounded-lg" />
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-muted h-24 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!path) {
    return (
      <PageLayout>
        <div className="container mx-auto px-4 py-6 md:py-8 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Learning Path Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The learning path you're looking for doesn't exist or may have been removed.
          </p>
          <Button asChild>
            <Link to="/learning-paths">Return to Learning Paths</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  const completedModules = path.modules.filter(m => m.isCompleted).length;
  const totalModules = path.modules.length;
  
  const difficultyColors = {
    Beginner: 'bg-emerald-100 text-emerald-700',
    Intermediate: 'bg-blue-100 text-blue-700',
    Advanced: 'bg-purple-100 text-purple-700'
  };
  
  const difficultyColor = difficultyColors[path.difficulty as keyof typeof difficultyColors] || difficultyColors.Beginner;

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/learning-paths" className="flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Learning Paths
          </Link>
        </Button>

        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold font-lexend">{path.title}</h1>
            <span className={`text-xs font-medium px-2 py-1 rounded ${difficultyColor}`}>
              {path.difficulty}
            </span>
          </div>
          <p className="text-muted-foreground mb-4">{path.description}</p>
          
          <div className="flex flex-wrap gap-6 items-center mb-6">
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2 text-muted-foreground" />
              <span>{path.estimatedHours} hours total</span>
            </div>
            <div className="flex items-center">
              <Book className="h-5 w-5 mr-2 text-muted-foreground" />
              <span>{totalModules} modules</span>
            </div>
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-muted-foreground" />
              <span>
                {completedModules} of {totalModules} completed
              </span>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm mb-2">
              <span>Overall Progress</span>
              <span>{path.progress || 0}%</span>
            </div>
            <Progress value={path.progress || 0} className="h-2" />
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Syllabus</h2>
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {path.modules.map((module, index) => (
                    <Collapsible 
                      key={module.id}
                      open={expandedModule === module.id}
                      onOpenChange={() => setExpandedModule(expandedModule === module.id ? null : module.id)}
                    >
                      <div className="flex items-start">
                        <div className="mr-3 mt-1">
                          {module.isCompleted ? (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          ) : (
                            <Circle className="h-5 w-5 text-muted-foreground" />
                          )}
                          {index < path.modules.length - 1 && (
                            <div className="h-full w-0.5 bg-border ml-2.5 my-1" />
                          )}
                        </div>
                        <div className="flex-1">
                          <CollapsibleTrigger className="flex w-full items-center justify-between text-left">
                            <div>
                              <h3 className="font-medium">
                                Module {index + 1}: {module.module?.title}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {module.module?.duration || "30 min"} • {module.module?.level || "Beginner"}
                              </p>
                            </div>
                            <ChevronRight className="h-5 w-5 transition-transform ui-open:rotate-90" />
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent className="pt-2 pb-4">
                            <p className="mb-4 text-muted-foreground">
                              {module.module?.description || "No description available."}
                            </p>
                            <Button 
                              onClick={() => handleStartModule(module.moduleId)}
                              variant={module.isCompleted ? "outline" : "default"}
                            >
                              {module.isCompleted ? "Review Module" : "Start Module"}
                            </Button>
                          </CollapsibleContent>
                          
                          {index < path.modules.length - 1 && <Separator className="my-4" />}
                        </div>
                      </div>
                    </Collapsible>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default LearningPathDetail;
